import React, { useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

import prevent from '../lib/prevent'
import useText from '../common/hooks/useText'
import { NfcTag } from '../common/redux/api/web-api'

import Text from './Text'
import BootstrapModalLayout from './BootstrapModalLayout'

const MAX_LENGTH = 40

const SelectedContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`

const EditNFCGroupModal: React.FC<Props> = ({ close, dismiss, nfcTags }) => {
    const text = useText()
    const [clearComment, setClearComment] = useState(false)
    const [clearExternalId, setClearExternalId] = useState(false)
    const [rfidTags, setRfidTags] = useState<NfcTag[]>(nfcTags)
    const [comment, setComment] = useState<string>('')
    const [externalId, setExternalId] = useState<string>('')

    const placeholder = (value: string | undefined, clearFlag: boolean) => {
        if (clearFlag) return text('msgValueWillBeCleared')
        if (typeof value === 'undefined') return text('msgKeepWithoutChanges')
        return text('msgClearTheValue')
    }

    const onInputExternalId = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newExternalId = event.target.value
        setExternalId(newExternalId)
    }

    const toggleSelect = (rfidTag: NfcTag) => {
        if (rfidTags.includes(rfidTag)) {
            setRfidTags(rfidTags.filter((tag) => tag.id !== rfidTag.id))
        } else {
            setRfidTags([...rfidTags, rfidTag])
        }
    }

    const toggleSelectAll = () => {
        if (rfidTags.length === nfcTags.length) {
            setRfidTags([])
        } else {
            setRfidTags(nfcTags)
        }
    }

    const save = () => {
        close(
            rfidTags.map((tag) => ({
                ...tag,
                comment: clearComment || comment.length ? comment : tag.comment,
                externalId: clearExternalId || externalId.length ? externalId : tag.externalId,
            }))
        )
    }

    return (
        <BootstrapModalLayout
            size="sm"
            title={text('lblEditRFIDGroupModal')}
            actions={[
                { label: text('btnSave'), callback: () => save(), disabled: !rfidTags.length },
                { label: text('btnCancel'), callback: () => dismiss() },
            ]}
        >
            <form onSubmit={prevent(save)}>
                <div className="modal-body">
                    <div className="form-group">
                        <label style={{ display: 'block' }}>
                            <Text code="msgSelectRFIDToEdit" />
                            <Link to="/" onClick={prevent(toggleSelectAll)} className="pull-right">
                                {nfcTags.length === rfidTags.length ? (
                                    <Text code="btnUnselectAll" />
                                ) : (
                                    <Text code="btnSelectAll" />
                                )}
                            </Link>
                        </label>
                        <SelectedContainer>
                            {nfcTags.map((nfcTag) => (
                                <span className="checkbox-form" key={nfcTag.id}>
                                    <input
                                        className="checkbox-black"
                                        type="checkbox"
                                        checked={rfidTags.includes(nfcTag)}
                                        onChange={() => toggleSelect(nfcTag)}
                                    />{' '}
                                    <span>{nfcTag.id}</span>
                                </span>
                            ))}
                        </SelectedContainer>
                    </div>
                    <div className="form-group">
                        <label className="pull-right text-lowercase checkbox-form">
                            <input
                                className="checkbox-black"
                                type="checkbox"
                                checked={clearComment}
                                onChange={() => {
                                    setClearComment(!clearComment)
                                    setComment('')
                                }}
                            />{' '}
                            <span>
                                <Text code="btnClear" />
                            </span>
                        </label>
                        <label>
                            <Text code="lblCommentField" />
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={placeholder(comment, clearComment)}
                            disabled={clearComment}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label className="pull-right text-lowercase checkbox-form">
                            <input
                                className="checkbox-black"
                                type="checkbox"
                                checked={clearExternalId}
                                onChange={() => setClearExternalId(!clearExternalId)}
                            />{' '}
                            <span>
                                <Text code="btnClear" />
                            </span>
                        </label>
                        <label>
                            <Text code="lblExternalIdField" />
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={placeholder(externalId, clearExternalId)}
                            disabled={clearExternalId}
                            value={externalId}
                            onChange={onInputExternalId}
                            maxLength={MAX_LENGTH}
                        />
                    </div>
                </div>
            </form>
        </BootstrapModalLayout>
    )
}

export default EditNFCGroupModal

export type Props = {
    nfcTags: NfcTag[]
    close: (result: NfcTag[]) => void
    dismiss: (reason?: string) => void
}
